import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'
// @ts-strict-ignore
import { Classes, Intent, TextArea } from '@blueprintjs/core'
import React, { type ChangeEvent, type ReactNode, useCallback, useMemo, useState } from 'react'
import { DefaultNumericOperators, Filters } from '../../config'
import type { FilterI } from '../../constants/filter'
import { getQueryParams } from '../../hooks/useURLSearch'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import { axiosGet, axiosPostWithQuery } from '../../util/api'
import { Layout } from '../Layout'
import { Popover } from '../ui/popover'
import { Link } from 'react-router-dom'
import type { CellProps } from '../table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable, DummyHeader } from '../table2/AgGridTable'
import { TableStore } from '../table2/table_store'
import { userStore } from '../../stores/user_store'

interface ProductOverviewItem {
    asin: string
    campaigns: number
    active_campaigns: number
    ad_groups: number
    active_ad_groups: number
}

const agGridComponent = (component: unknown): ReactNode => component as ReactNode

export const ProductOverview = () => {
    const [asinInput, setAsinInput] = useState<string>('')
    const [showVT, setShowVT] = useState<boolean>(false)
    const [showAllProducts, setShowAllProducts] = useState<boolean>(false)
    const [showEditModal, setShowEditModal] = useState<boolean>(false)
    const { perPage } = getQueryParams(['perPage'])
    const [store] = useState<TableStore<ProductOverviewItem>>(
        new TableStore(userStore, 'ProductOverview', { perPage }),
    )
    const savedFiltersStore = useSavedFiltersStore()

    const load = (params) => {
        let payload
        if (!showAllProducts) {
            const invalidAsins = getInvalidAsins()
            const createPayload = getUniqueAsins()
                .filter((asin) => {
                    // weed out invalid asins
                    return !invalidAsins.includes(asin)
                })
                .map((asin) => {
                    return {
                        asin: asin,
                    }
                })

            payload = {
                ...params,
                payload: createPayload,
            }
        } else {
            payload = {
                ...params,
                payload: [{ asin: 'all' }],
            }
        }

        return axiosPostWithQuery(
            'api/v2/product-overview',
            payload,
            {
                filter: {
                    ...params.filter,
                },
            },
            true,
            null,
        )
    }

    const tableHooks = useTableHooksFromStore(load, { perPage }, true, store)

    const handleAsinInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setAsinInput(e.target.value)
    }

    const displayTable = () => {
        setShowEditModal(false)
        setShowVT(true)
        tableHooks.reload()
    }

    const displayTableWithAllAsins = () => {
        setShowAllProducts(true)
        displayTable()
    }

    const asinsArray = useMemo(
        () =>
            asinInput
                .replace(/\n/g, ',')
                .split(',')
                .filter((e) => e),
        [asinInput],
    )

    const getInvalidAsins = useCallback((): string[] => {
        const invalid: string[] = []
        const pattern = '/[0-9BC][0-9]{2}[0-9A-Z]{7}|[0-9]{9}(X|0-9]/gi)' // Amazon ASIN
        asinsArray.forEach((asin) => {
            if (asin != '' && asin.length !== 10 && !asin.match(pattern)) {
                invalid.push(asin)
            }
        })

        return invalid
    }, [asinsArray])

    const getUniqueAsins = (): string[] => {
        return Array.from(new Set(asinsArray))
    }

    const onEdit = () => {
        setShowEditModal(true)
    }

    const AsinInputComponent = (modal = false) => {
        let classNames = 'flex flex-col'
        if (!modal) {
            classNames += ' w-1/3'
        } else {
            classNames += ' w-2/3'
        }

        return (
            <div className={classNames}>
                <p hidden={modal}>
                    Product Overview gives you the ability to quickly see which of your products
                    have campaigns and ad groups running. You will need to input a list of all the
                    ASINs you are currently selling. You can get a list of these from Productor or
                    another browser extension. We have a Merch Jar extension in the works which will
                    make this whole process a lot easier!
                </p>
                <TextArea
                    growVertically={false}
                    large={true}
                    intent={Intent.PRIMARY}
                    onChange={handleAsinInput}
                    value={asinInput}
                    rows={8}
                    placeholder="Enter your ASIN catalog here with one ASIN per line"
                />
                <Button
                    className="mt-3"
                    disabled={getUniqueAsins().length == 0}
                    intent="primary"
                    text="View Product Overview for above ASINs"
                    onClick={displayTable}
                />
                <Button
                    className="mt-3"
                    disabled={getUniqueAsins().length != 0}
                    intent="primary"
                    text="View Product Overview for all your Products"
                    onClick={displayTableWithAllAsins}
                />
            </div>
        )
    }

    const columns = useMemo<AgGridColumnDef<ProductOverviewItem>[]>(
        () => [
            {
                field: 'asin',
                headerName: 'Asin',
                headerComponent: agGridComponent(DummyHeader),
                width: 385,
            },
            {
                field: 'campaigns',
                headerName: 'Campaigns',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(CampaignCountCellRenderer),
                width: 200,
            },
            {
                field: 'active_campaigns',
                headerName: 'Active Campaigns',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(ActiveCampaignCountCellRenderer),
                width: 200,
            },
            {
                field: 'ad_groups',
                headerName: 'Ad Groups',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(AdGroupCountCellRenderer),
                width: 200,
            },
            {
                field: 'active_ad_groups',
                headerName: 'Active Ad Groups',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(ActiveAdGroupCountCellRenderer),
                width: 200,
            },
        ],
        [],
    )

    const breadcrumbs = [{ text: 'Tools' }, { href: '', text: 'Product Overview' }]

    return (
        <Layout breadcrumbs={breadcrumbs}>
            <Dialog isOpen={showEditModal}>
                <div className={Classes.DIALOG_BODY}>{AsinInputComponent(true)}</div>
                <div className={Classes.DIALOG_FOOTER}>
                    <Button text="Close" onClick={() => setShowEditModal(false)} />
                </div>
            </Dialog>
            <div>
                <div>
                    {!showVT ? (
                        AsinInputComponent()
                    ) : (
                        <div className="flex mb-2">
                            {!showAllProducts && (
                                <>
                                    <p className="m-0 p-0 ml-3">
                                        Product list contains {getUniqueAsins()?.length} products
                                    </p>
                                    <Button className="ml-2" small text="Edit" onClick={onEdit} />
                                </>
                            )}
                        </div>
                    )}
                </div>
                <div className="w-full">
                    {showVT && (
                        <AgGridTable
                            name="Product Overview"
                            dateSelect={false}
                            searchPlaceholder="Search By ASIN"
                            filters={filters}
                            search={true}
                            columnSelection={false}
                            showHeader={false}
                            tableHooks={tableHooks}
                            store={store}
                            savedFiltersStore={savedFiltersStore}
                            columns={columns}
                        />
                    )}
                </div>
            </div>
        </Layout>
    )
}

const CampaignCountCellRenderer = ({ data }: CellProps<ProductOverviewItem>) => (
    <CampaignCount {...data} />
)

const ActiveCampaignCountCellRenderer = ({ data }: CellProps<ProductOverviewItem>) => (
    <CampaignCount {...data} dataKey="active_campaigns" params={{ active: 1 }} />
)

const AdGroupCountCellRenderer = ({ data }: CellProps<ProductOverviewItem>) => (
    <AdGroupCount {...data} />
)

const ActiveAdGroupCountCellRenderer = ({ data }: CellProps<ProductOverviewItem>) => (
    <AdGroupCount {...data} dataKey="active_ad_groups" params={{ active: 1 }} />
)

const CampaignCount = (item) => {
    const [campaigns, setCampaigns] = useState([])
    const [loaded, setLoaded] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [total, setTotal] = useState<number>(0)

    const load = () => {
        if (!loaded && !loading) {
            setLoading(true)
            axiosGet('product-overview/campaigns', {
                asin: item.asin,
                ...(item?.params || {}),
            })
                .then((res) => {
                    setLoaded(true)
                    setLoading(false)
                    setCampaigns(res.data.data)
                    setTotal(res.data.total)
                    return res
                })
                .catch((err) => {
                    setLoading(false)
                    return err
                })
        }
    }

    const content = (
        <div className="p-3">
            {campaigns.map((c) => (
                <Link to={`/campaigns/${c.campaign_id}`} key={c.campaign_id}>
                    <div>{c.name}</div>
                    {total > campaigns.length && (
                        <div className="text-gray-500">
                            And {total - campaigns.length} more not shown
                        </div>
                    )}
                </Link>
            ))}
        </div>
    )
    return (
        <Popover interactionKind="hover" content={content} onOpening={load}>
            {item?.dataKey ? item[item.dataKey] : item.campaigns}
        </Popover>
    )
}

const AdGroupCount = (item) => {
    const [adGroups, setAdGroups] = useState([])
    const [loaded, setLoaded] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [total, setTotal] = useState<number>(0)

    const load = () => {
        if (!loaded && !loading) {
            setLoading(true)
            axiosGet('product-overview/ad-groups', {
                asin: item.asin,
                ...(item?.params || {}),
            })
                .then((res) => {
                    setLoaded(true)
                    setLoading(false)
                    setAdGroups(res.data.data)
                    setTotal(res.data.total)
                    return res
                })
                .catch((err) => {
                    setLoading(false)
                    return err
                })
        }
    }

    const content = (
        <div className="p-3">
            {adGroups.map((c) => (
                <Link to={`/ad-groups/${c.ad_group_id}`} key={c.ad_group_id}>
                    <div>{c.name}</div>
                    {total > adGroups.length && (
                        <div className="text-gray-500">
                            And {total - adGroups.length} more not shown
                        </div>
                    )}
                </Link>
            ))}
        </div>
    )
    return (
        <Popover interactionKind="hover" content={content} onOpening={load}>
            {item?.dataKey ? item[item.dataKey] : item.campaigns}
        </Popover>
    )
}

const filters: FilterI[] = [
    {
        ...Filters.CampaignCount,
        key: 'campaigns',
    },
    {
        ...Filters.AdGroupCount,
        key: 'ad_groups',
    },
    {
        operators: DefaultNumericOperators,
        key: 'active_campaigns',
        name: 'Active Campaigns',
        type: 'number',
    },
    {
        operators: DefaultNumericOperators,
        key: 'active_ad_groups',
        name: 'Active Ad Groups',
        type: 'number',
    },
]
